import {Injectable, Injector} from '@angular/core';
import * as _ from 'lodash-es';
import {Widget} from '../components/layout/widget/widget';
import {WidgetSettingsDefinition} from '../components/layout/widget/widget-settings-definition';
import {WidgetFactory} from '../components/layout/widget/widget.factory';
import {WatchlistService} from '../components/watchlist';
import {CellComponent} from '../pages/dashboard/layout/cell/cell.component';
import {DetachedWidgetDefinition, DetachedWidgetPageDefinition} from '../pages/widget-page/detached-widget-def';
import {AppConfigService} from './config.service';
import {UrlHelperService} from './url-helper.service';
import {WindowRefService} from './window-ref.service';

interface WindowFeatures {
    [featureName: string]: boolean | number;
}

interface WidgetBoundaries {
    left: number;
    top: number;
    width: number;
    height: number;
}

interface WidgetSettingsWithWatchlistId extends WidgetSettingsDefinition {
    watchlistId?: string;
}

@Injectable()
export class WidgetDetachService {

    private static serializeWindowFeatures(features: WindowFeatures): string {
        return _.map(features, (featureValue, featureName) =>
            featureValue === true
                ? featureName
                : featureValue === false
                ? `${featureName}=no`
                : `${featureName}=${featureValue}`
        )
            .join(',');
    }

    private detachedWidgetWindowFeatures: WindowFeatures = {

        // Disabled features. Note that many of them are always enabled due to browser's settings.
        toolbar: false,
        location: false,
        menubar: false,
        personalbar: false,
        directories: false,
        status: false,

        // Enabled features
        scrollbars: true,
        resizable: true
    };

    private cellComponent: CellComponent;
    private widgetFactory: WidgetFactory = this.injector.get(WidgetFactory);

    constructor(
        private appConfig: AppConfigService,
        private windowRefService: WindowRefService,
        private watchlistService: WatchlistService,
        private injector: Injector
    ) {}

    public detachWidget(widget: Widget): void {
        this.detachWidgets([widget]);
    }

    public detachWidgets(widgets: Widget[], activeWidgetIndex: number = 0): void {
        const widgetsDef = widgets.map(widget => this.widgetFactory.getWidgetDefinition(widget));
        const widgetsSettings: WidgetSettingsWithWatchlistId[] = widgetsDef
            .map<WidgetSettingsWithWatchlistId>(widgetDef => _.mapValues(
                widgetDef.settings,
                value => value === undefined ? null : value
            ));

        const detachedWidgetDefs: DetachedWidgetDefinition[] = widgetsSettings.map((settings, index) => {
            const detachedWidgetDef: DetachedWidgetDefinition = {
                widgetTypeId: widgetsDef[index].widgetTypeId,
                settings
            };

            if (settings.watchlistId) {
                detachedWidgetDef.watchlist = this.watchlistService.findWatchlistById(settings.watchlistId);
            }

            return detachedWidgetDef;
        });

        const detachedWidgetPageDef: DetachedWidgetPageDefinition = {
            detachedWidgetDefs,
            activeWidgetIndex
        };

        const encodedWidgetsSettings = UrlHelperService.encodeDataToBase64(detachedWidgetPageDef);

        this.cellComponent = widgets[activeWidgetIndex].cell.display.cellComponent;

        const windowFeaturesString: string = WidgetDetachService.serializeWindowFeatures(
            this.getDetachedWidgetWindowFeaturesString()
        );

        const publicPath = this.appConfig
            .getPublicPath(`widget/${encodedWidgetsSettings}`);

        window.open(publicPath, '_blank', windowFeaturesString);
    }

    private getDetachedWidgetWindowFeaturesString(): WindowFeatures {
        const detachedWidgetBoundaries: WidgetBoundaries = this.getDetachedWidgetBoundaries();

        return {
            ...this.detachedWidgetWindowFeatures,
            ...detachedWidgetBoundaries
        };
    }

    private getDetachedWidgetBoundaries(): WidgetBoundaries {
        const [windowLeft, windowTop] = this.getWindowPosition();
        const cellBodyElement = this.cellComponent.cellElementRef.nativeElement;
        const cellBodyRect: ClientRect = cellBodyElement.getBoundingClientRect();

        return {
            left: windowLeft + cellBodyRect.left,
            top: windowTop + cellBodyRect.top,
            width: cellBodyRect.width,
            height: cellBodyRect.height
        };
    }

    private getWindowPosition(): [number, number] {
        const window: Window = this.windowRefService.nativeWindow;
        const fallbackValue = (value: number, altValue: number) => value || value === 0 ? value : altValue || 0;

        return [
            fallbackValue(window.screenX, window.screenLeft),
            fallbackValue(window.screenY, window.screenTop)
        ];
    }

}
